import type { PromotionListResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import { Button, Card } from "@sellar-io/components";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { setFallbackImage } from "../../../components/image/imageWithFallback.shared";
import { usePromotionList } from "../../../hooks/api/v2/promotion/usePromotionList";
import {
  useUnifiedCheckoutBasketAddItem,
  useUnifiedCheckoutBasketUpdateItem,
} from "../../../hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";
import { getCanPromotionRewardItemBeClaimed } from "../../stockList/categoryPage/Storefront/StorefrontPromotion/getCanPromotionRewardItemBeClaimed";
import { isPromotionalOrderItem } from "../../stockList/categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import type {
  SupplierOrderEntry,
  UnifiedCheckoutDraftItem,
} from "./UnifiedCheckoutOverviewPage.shared";

export const SupplierPromotions = ({
  entry,
  basketItems,
  basketUuid,
  onAvailabilityChange,
}: {
  entry: SupplierOrderEntry;
  basketItems: UnifiedCheckoutDraftItem[];
  basketUuid: string;
  onAvailabilityChange?: (available: boolean) => void;
}) => {
  const { order, supplierInfo } = entry;
  const priceTierId = supplierInfo.storefront?.PriceTier?.id;
  const promotionsQuery = usePromotionList(
    {
      query: [
        {
          type: "SupplierId",
          modifier: "EQUALS",
          value: supplierInfo.supplier.id,
        },
        ...(priceTierId
          ? [
              {
                type: "PriceTierId" as const,
                modifier: "EQUALS" as const,
                value: priceTierId,
              },
            ]
          : []),
        { type: "isActive", modifier: "EQUALS", value: true },
        {
          type: "onlyPromotionsWithRewards",
          modifier: "EQUALS",
          value: true,
        },
        {
          type: "validAt",
          modifier: "EQUALS",
          value: dayjs().startOf("day").toISOString(),
        },
      ],
    },
    { enabled: Boolean(order.SupplierId) },
  );
  const addItem = useUnifiedCheckoutBasketAddItem();
  const updateItem = useUnifiedCheckoutBasketUpdateItem();
  const promotions = useMemo(
    () => promotionsQuery.data?.pages.flatMap((page) => page) ?? [],
    [promotionsQuery.data],
  );
  const nonPromotionOrderItems = useMemo(
    () =>
      (order.OrderItems ?? []).filter((item) => !isPromotionalOrderItem(item)),
    [order.OrderItems],
  );
  const claimedRewardIds = useMemo(
    () =>
      basketItems.flatMap((item) =>
        item.PromotionRewardItemId == null ? [] : [item.PromotionRewardItemId],
      ),
    [basketItems],
  );

  useEffect(() => {
    onAvailabilityChange?.(promotions.length > 0);
  }, [onAvailabilityChange, promotions.length]);

  useEffect(() => {
    if (updateItem.isPending || !promotionsQuery.isSuccess) return;
    const invalidClaim = basketItems.find((item) => {
      if (item.PromotionRewardItemId == null) return false;
      if (nonPromotionOrderItems.length === 0) return true;
      return !getCanPromotionRewardItemBeClaimed({
        promotionRewardItemId: item.PromotionRewardItemId,
        nonPromotionBasketOrderItems: nonPromotionOrderItems,
        claimedPromotionRewardItemIds: claimedRewardIds.filter(
          (id) => id !== item.PromotionRewardItemId,
        ),
        promotions,
      }).canBeClaimed;
    });
    if (invalidClaim) {
      updateItem.mutate({ basketUuid, itemId: invalidClaim.id, quantity: 0 });
    }
  }, [
    basketItems,
    basketUuid,
    claimedRewardIds,
    nonPromotionOrderItems,
    promotions,
    promotionsQuery.isSuccess,
    updateItem,
  ]);

  if (promotions.length === 0) return null;

  const changeReward = ({
    reward,
  }: {
    reward: PromotionListResponseType[number]["PromotionRewardItems"][number];
  }) => {
    const basketItem = basketItems.find(
      (item) => item.PromotionRewardItemId === reward.id,
    );
    if (basketItem) {
      updateItem.mutate({ basketUuid, itemId: basketItem.id, quantity: 0 });
      return;
    }
    if (order.SupplierId == null) return;
    const eligibility = getCanPromotionRewardItemBeClaimed({
      promotionRewardItemId: reward.id,
      nonPromotionBasketOrderItems: nonPromotionOrderItems,
      claimedPromotionRewardItemIds: claimedRewardIds,
      promotions,
    });
    if (!eligibility.canBeClaimed) return;

    addItem.mutate({
      SupplierId: order.SupplierId,
      ProductId: reward.Product.id,
      PromotionRewardItemId: reward.id,
      quantity: reward.quantity,
      expectedDeliveryAt: order.expectedDeliveryDate
        ? new Date(order.expectedDeliveryDate)
        : null,
      orderCutoffAt: order.orderCutoffDate
        ? new Date(order.orderCutoffDate)
        : null,
    });
  };

  return (
    <Card className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="mb-3 text-sm">
        <span className="font-semibold">Promo items</span>
        <span> / {supplierInfo.supplier.Business.name}</span>
      </div>
      <div className="flex flex-col gap-2">
        {promotions.flatMap((promotion) =>
          promotion.PromotionRewardItems.map((reward) => {
            const isClaimed = claimedRewardIds.includes(reward.id);
            const eligibility = getCanPromotionRewardItemBeClaimed({
              promotionRewardItemId: reward.id,
              nonPromotionBasketOrderItems: nonPromotionOrderItems,
              claimedPromotionRewardItemIds: claimedRewardIds,
              promotions,
            });
            const rewardName =
              reward.Product.nameV2 ?? reward.Product.name ?? "reward item";
            const rewardPrice =
              reward.price === 0
                ? "free"
                : formatCurrency(reward.price, order.currency ?? "gbp");
            const imageUrl = setFallbackImage({
              img: reward.Product.imageUrl ?? reward.Product.Parent?.imageUrl,
              fallback: "product-badge",
            });
            const isPending =
              (addItem.isPending &&
                addItem.variables?.PromotionRewardItemId === reward.id) ||
              (updateItem.isPending &&
                basketItems.find(
                  (item) => item.PromotionRewardItemId === reward.id,
                )?.id === updateItem.variables?.itemId);
            const isSpendThresholdMissing =
              !eligibility.canBeClaimed &&
              eligibility.message === "SPEND_THRSHOLD_NOT_MET";
            const nonPromotionSubtotal = nonPromotionOrderItems.reduce(
              (total, item) => total + item.price * item.quantity,
              0,
            );

            return (
              <div
                key={reward.id}
                className="flex flex-col gap-3 rounded-lg border border-zinc-200 p-3"
              >
                <div className="flex items-center gap-3 text-sm">
                  <img
                    src={imageUrl ?? undefined}
                    alt=""
                    className="h-10 w-10 rounded-lg object-cover"
                  />
                  <span>
                    Spend{" "}
                    <strong>
                      {formatCurrency(
                        reward.spendThreshold,
                        order.currency ?? "gbp",
                      )}
                    </strong>{" "}
                    and receive{" "}
                    <strong>
                      {reward.quantity > 1 ? `${reward.quantity} x ` : ""}
                      {rewardName}
                    </strong>{" "}
                    for <strong>{rewardPrice}</strong>
                  </span>
                </div>
                <Button
                  color="slim"
                  className={isClaimed ? "bg-zinc-200" : ""}
                  disabled={
                    isPending || (!isClaimed && !eligibility.canBeClaimed)
                  }
                  onClick={() => changeReward({ reward })}
                >
                  {isClaimed
                    ? "Remove"
                    : eligibility.canBeClaimed
                      ? "Add to basket"
                      : isSpendThresholdMissing
                        ? `Spend ${formatCurrency(
                            reward.spendThreshold - nonPromotionSubtotal,
                            order.currency ?? "gbp",
                          )} more`
                        : "Another reward has been selected"}
                </Button>
              </div>
            );
          }),
        )}
      </div>
    </Card>
  );
};
